import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { nanoid } from 'nanoid'
import { uploadBlobToCdn } from '@/lib/utils'
import type { Asset, AssetSource, AssetType } from '@/lib/types'
import { PDF_FILE_THUMBNAIL_URL } from '@/lib/constant'

export type IUploadAssets = (
  files: File[],
  args: { assetSource: AssetSource; assetDir: string },
) => Promise<
  | {
      success: true
      assets: {
        source: AssetSource
        type: 'image' | 'video' | 'audio' | 'pdf' | 'csv'
        link: string
        thumbnail: string
        name: string
      }[]
    }
  | { success: false; error: string }
>

// Common audio and video formats - Restricted to specific formats only
export const ALLOWED_UPLOAD_ASSETS_FORMATS = [
  // Images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  // Videos - Only MP4
  'video/mp4',
  // Audio - Only MP3
  'audio/mpeg', // mp3
  // Documents - Only CSV and PDF
  'text/csv',
  'application/pdf',
]

export const ALLOWED_UPLOAD_ASSETS_FORMATS_TEXT =
  'JPG, JPEG, PNG, WEBP, MP4, MP3, CSV, PDF'

export const ALLOWED_UPLOAD_IMAGE_FORMATS_TEXT = 'JPG, JPEG, PNG, WEBP'

// Video formats - Currently only MP4 is allowed in general uploads
// These constants are kept for reference but not actively used
export const ALLOWED_UPLOAD_VIDEO_FORMATS = ['video/mp4']

export const ALLOWED_UPLOAD_VIDEO_FORMATS_TEXT = 'MP4'

export const ALLOWED_UPLOAD_IMAGE_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]

// Table imports support CSV and Excel formats - these are handled separately
// in /components/database/components/import-csv/index.tsx with accept=".csv,.xlsx,.xls"
export const ALLOWED_TABLE_FORMATS = [
  'text/csv',
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
]

export const MAX_UPLOAD_ASSETS = 25

const NANOID_LENGTH = 8

export const parseFileNameFromUrl = (url: string) => {
  try {
    const urlParts = url.split('/')
    const filename = urlParts[urlParts.length - 1]

    // {originalName}_{8 characters}.{extension}
    const matches = filename.match(
      new RegExp(`^(.+)_([A-Za-z0-9_-]{${NANOID_LENGTH}})\\.(.*?)$`),
    )
    if (matches) {
      const [_, originalName, __, extension] = matches
      return `${originalName}.${extension}`
    }
    // 兼容逻辑
    return filename
  } catch (error) {
    console.error('Error parsing filename from URL:', error)
    // 避免解析失败页面挂了
    return url
  }
}

/**
 * 上传文件，
 * 首页、AI 聊天、Upload Tab 均依赖该函数
 * @description  默认 unlimited 为 true，没有上传数量限制
 */
export const useUploadAssets = (options?: {
  maxUploadAssets?: number
  unlimited?: boolean
}) => {
  const { maxUploadAssets = MAX_UPLOAD_ASSETS, unlimited = true } =
    options || {}

  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const uploadedAssets = useRef<Asset[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)

  const getAssetThumbnail = (type: AssetType, link: string) => {
    switch (type) {
      case 'image':
        return link
      case 'pdf':
        return PDF_FILE_THUMBNAIL_URL
      case 'csv':
        return 'https://heyboss.heeyo.ai/design-system/audio_tPAodmps.png'
      case 'video':
        return 'https://heyboss.heeyo.ai/design-system/audio_tPAodmps.png'
      case 'audio':
        return 'https://heyboss.heeyo.ai/design-system/audio_tPAodmps.png'
      default:
        return 'https://heyboss.heeyo.ai/design-system/audio_tPAodmps.png'
    }
  }

  const uploadAssets = async (
    files: File[],
    args: { assetSource: AssetSource; assetDir: string },
  ): Promise<
    | {
        success: true
        assets: {
          source: AssetSource
          type: 'video' | 'audio' | 'image' | 'pdf' | 'csv'
          link: string
          thumbnail: string
          name: string
        }[]
      }
    | { success: false; error: string }
  > => {
    const { assetSource, assetDir } = args

    if (
      !unlimited &&
      uploadedAssets.current.length + files.length > maxUploadAssets
    ) {
      return {
        success: false,
        error: `You can upload up to ${maxUploadAssets} files at a time`,
      }
    }

    const validFiles = files.filter((file) =>
      ALLOWED_UPLOAD_ASSETS_FORMATS.includes(file.type as any),
    )

    if (validFiles.length === 0) {
      return {
        success: false,
        error: `Unsupported file type. Please use: ${ALLOWED_UPLOAD_ASSETS_FORMATS_TEXT}`,
      }
    }

    // Create new AbortController for this upload
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const progressPerFile = 100 / validFiles.length
      let completedFiles = 0

      // Create array of upload promises
      const uploadPromises = validFiles.map(async (file) => {
        // Check if upload was cancelled
        if (signal.aborted) {
          throw new Error('Upload cancelled')
        }

        const extension = file.name.split('.').pop() || 'bin'
        const originalFileName = file.name.split('.').slice(0, -1).join('.')
        const filename = `${assetDir}/${originalFileName}_${nanoid(NANOID_LENGTH)}.${extension}`
        const url = await uploadBlobToCdn(file, filename, file.type, signal)

        // Get file type category
        let assetType: 'video' | 'audio' | 'image' | 'pdf' | 'csv' = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('image/')) assetType = 'image'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        // Check if upload was cancelled before saving to database
        if (signal.aborted) {
          throw new Error('Upload cancelled')
        }

        // Save asset to database
        await fetch('/api/me/assets', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            asset_url: url,
            type: assetType,
            source: assetSource,
          }),
          signal,
        })

        // Update progress after each file completes
        completedFiles++
        setUploadProgress(completedFiles * progressPerFile)

        return {
          source: assetSource,
          type: assetType,
          link: url,
          thumbnail: getAssetThumbnail(assetType, url),
          name: file.name, // Preserve original file name
        }
      })

      // Wait for all uploads to complete
      // Check if cancelled before waiting
      if (signal.aborted) {
        throw new Error('Upload cancelled')
      }
      const newAssets = await Promise.all(uploadPromises)

      // Check again after all uploads complete
      if (signal.aborted) {
        throw new Error('Upload cancelled')
      }

      // 缓存已上传文件
      uploadedAssets.current = [...uploadedAssets.current, ...newAssets]

      return { success: true, assets: newAssets }
    } catch (error) {
      console.error('Error uploading files:', error)
      // Check if error is due to cancellation
      if (
        signal.aborted ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        return { success: false, error: 'Upload cancelled' }
      }

      // Convert error messages to human-friendly language
      let errorMessage = 'Failed to upload files'
      if (error instanceof Error) {
        const msg = error.message.toLowerCase()
        if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage =
            'Network error. Please check your connection and try again'
        } else if (msg.includes('timeout')) {
          errorMessage = 'Upload timed out. Please try again'
        } else if (msg.includes('413') || msg.includes('too large')) {
          errorMessage = 'File is too large. Maximum size is 10MB'
        } else if (msg.includes('403') || msg.includes('forbidden')) {
          errorMessage = 'Permission denied. Please try again'
        } else if (msg.includes('500') || msg.includes('server')) {
          errorMessage = 'Server error. Please try again later'
        } else {
          // Use original message if it's already human-friendly
          errorMessage = error.message
        }
      }
      return { success: false, error: errorMessage }
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      abortControllerRef.current = null
    }
  }

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  return {
    getAssetThumbnail,
    uploadProgress,
    uploadAssets,
    uploadedAssets,
    isUploading,
    setIsUploading,
    cancelUpload,
  }
}
